
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCourierInfo } from './useCourierInfo';
import { toast } from '@/components/ui/sonner';

export interface UpcomingPayment {
  subscription_id: string;
  rental_id: string;
  human_number?: string;
  courier_id: string;
  courier_name: string;
  amount: number;
  next_payment_date: string;
  status: string;
  interval: string;
  days_until_payment: number;
  payment_method_id?: string | null;
  card_last4?: string | null;
}

export const useUpcomingSubscriptionPayments = () => {
  const [upcomingPayments, setUpcomingPayments] = useState<UpcomingPayment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { courierId } = useCourierInfo();
  
  useEffect(() => {
    const fetchUpcomingPayments = async () => {
      if (!courierId) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Для начала получаем аренды курьера
        const { data: rentalsData, error: rentalsError } = await supabase
          .from('rentals')
          .select('id, human_number')
          .eq('courier_id', courierId);
          
        if (rentalsError) {
          throw rentalsError;
        }
        
        if (!rentalsData || rentalsData.length === 0) {
          setUpcomingPayments([]);
          setLoading(false);
          return;
        }
        
        // Получаем IDs аренд
        const rentalIds = rentalsData.map(rental => rental.id);
        
        // Получаем подписки для этих аренд
        const { data: subscriptionsData, error: subscriptionsError } = await supabase
          .from('subscriptions')
          .select(`
            id,
            amount,
            next_payment_date,
            status,
            interval,
            rental_id,
            payment_method_id,
            card_last4
          `)
          .in('rental_id', rentalIds)
          .eq('status', 'active')
          .gte('next_payment_date', new Date().toISOString().split('T')[0])
          .order('next_payment_date', { ascending: true });
          
        if (subscriptionsError) {
          throw subscriptionsError;
        }
        
        // Получаем информацию о курьере
        const { data: courierData, error: courierError } = await supabase
          .from('couriers')
          .select('name')
          .eq('id', courierId)
          .single();
          
        if (courierError) {
          console.warn('Ошибка получения данных о курьере:', courierError);
        }
        
        // Формируем итоговый массив предстоящих платежей
        const courierPayments = subscriptionsData?.map(subscription => {
          // Находим соответствующую аренду
          const rental = rentalsData.find(r => r.id === subscription.rental_id);
          
          // Вычисляем days_until_payment
          const nextPaymentDate = new Date(subscription.next_payment_date);
          const today = new Date();
          const timeDiff = nextPaymentDate.getTime() - today.getTime();
          const days_until_payment = Math.ceil(timeDiff / (1000 * 3600 * 24));
          
          return {
            subscription_id: subscription.id,
            rental_id: subscription.rental_id,
            human_number: rental?.human_number,
            courier_id: courierId,
            courier_name: courierData?.name || 'Неизвестный курьер',
            amount: subscription.amount,
            next_payment_date: subscription.next_payment_date,
            status: subscription.status,
            interval: subscription.interval,
            days_until_payment,
            payment_method_id: subscription.payment_method_id,
            card_last4: subscription.card_last4
          } as UpcomingPayment;
        }) || [];
        
        console.log('Получены данные о предстоящих платежах:', courierPayments);
        setUpcomingPayments(courierPayments);
        setError(null);
      } catch (err: any) {
        console.error('Непредвиденная ошибка:', err);
        setError(`Произошла ошибка: ${err.message}`);
        toast.error('Ошибка загрузки данных о платежах');
        setUpcomingPayments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingPayments();
  }, [courierId]);

  return { 
    upcomingPayments, 
    loading, 
    error,
    hasUpcomingPayments: upcomingPayments.length > 0
  };
};
